import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { IndividualPaymentParcelSaveViewModel } from '@/shared/api/generated';
import {
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidHeaderQueryKey,
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey,
  usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcels,
} from '@/shared/api/generated';

import type { PlanWriteError } from '../utils/planWriteError';
import { classifyPlanWriteError } from '../utils/planWriteError';

/**
 * Criação de parcela manual em um contrato financeiro.
 *
 * Duas queries são invalidadas no sucesso, e as duas por motivo próprio: a lista
 * de parcelas porque a nova linha (ou a faixa toda, com recorrência) só existe do
 * lado do servidor — a tela nunca monta a parcela criada localmente —, e o header
 * porque seus totais são somas das parcelas.
 *
 * A classificação do erro é a mesma da escrita de plano (`classifyPlanWriteError`):
 * o `400` traz mensagem de regra de negócio que o operador precisa ler — o caso
 * conhecido é número de parcela repetido no mesmo tipo — e o `5xx` não tem
 * tratamento global, então precisa ser sinalizado pela tela.
 *
 * @param financialPersonId - ID do contrato; `null` torna o `create` inerte.
 * @returns `{ create, isPending, isForbidden, isServerError, error, reset }`
 *
 * @example
 * ```tsx
 * const { create, isPending } = useCreateParcel(101);
 * create(payload, { onSuccess: () => navigate({ to: '..' }) });
 * ```
 */
export function useCreateParcel(financialPersonId: number | null) {
  const queryClient = useQueryClient();
  const mutation = usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcels();

  const create = useCallback(
    (
      data: IndividualPaymentParcelSaveViewModel,
      options?: {
        readonly onSuccess?: () => void;
        readonly onError?: (error: PlanWriteError) => void;
      },
    ) => {
      if (financialPersonId === null) return;

      mutation.mutate(
        { financialPersonId, data },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey:
                getApiV1IndividualpaymentFinancialPeopleFinancialpersonidParcelsQueryKey(
                  financialPersonId,
                ),
            });
            void queryClient.invalidateQueries({
              queryKey:
                getApiV1IndividualpaymentFinancialPeopleFinancialpersonidHeaderQueryKey(
                  financialPersonId,
                ),
            });
            options?.onSuccess?.();
          },
          onError: error => options?.onError?.(classifyPlanWriteError(error)),
        },
      );
    },
    [financialPersonId, mutation, queryClient],
  );

  const failure = classifyPlanWriteError(mutation.error);

  return {
    create,
    isPending: mutation.isPending,
    isError: mutation.isError,
    /** Falha de regra de negócio, com a mensagem do backend quando houver. */
    error: failure,
    isForbidden: failure.kind === 'forbidden',
    isServerError: failure.kind === 'server',
    reset: mutation.reset,
  } as const;
}
